import axios from 'axios'
import qs from 'qs'
import { getProductPrice } from '../utils/priceTypeHelpers'

/**
 * The Wipon API base.
 *
 * ATL-1170 — this used to read `https://app.kassa.wipon.kz/api/v1/`. On 2026-09-03 22:44:55 UTC
 * (03:44:55 Almaty, 2026-09-04) Wipon split their web app from their API: `app.kassa.wipon.kz`
 * became a pure SPA, serving the same 5374-byte shell with **HTTP 200 text/html** for every
 * `/api/v1/*` GET and **405** for every POST. Both names still resolve to 90.156.251.175, so this
 * was a routing change with no error, no redirect and no notice.
 *
 * The effect on the fleet was total and immediate: of the 23 stores configured for `wipon-kassa`,
 * the five that traded that morning produced **67 sales and 0 fiscal receipts**, and the boundary
 * sits exactly across the deploy — last success 2026-09-03 20:27:38 Almaty, first failure
 * 2026-09-04 07:16:03. Nothing reached Wipon: their own `GET /ticket` returned 0 tickets for the
 * day.
 *
 * `api.kassa.wipon.kz` is the base Wipon's own redeployed bundle carries, and it answers as an API
 * — JSON 401 unauthenticated, JSON 400 on a bad login, 200 with a token on a good one.
 *
 * `global/__tests__/wiponFiscalization.test.js` is what makes this URL fail out loud if it ever
 * goes stale again; before ATL-1170 no test named it at all.
 */
const BASE_URL = 'https://api.kassa.wipon.kz/api/v1/'

/**
 * ATL-1237 — everything below this line exists because Wipon serialises one cashbox and the till
 * did not.
 *
 * MEASURED, on Petit Зенкова (profile 5319 / cashbox 6715), 2026-09-07: 32 sales between 12:28:06Z
 * and 12:42:53Z, 15 of them with no `fiscalInfo`, interleaved second-by-second with sales that
 * signed cleanly. Sentry issue 7713703268 (152 events, `fiscalFailureReason=not-retryable`) ends
 * every one of them at `POST …/cashbox/6715/ticket` → **500**, and the customer's screenshot shows
 * the body we turned into an alert: «Ошибка в ОФД - Касса в обработке, попробуйте позже».
 *
 * The cashbox is BUSY and is asking to be sent the ticket again. We were treating that as
 * permanent, because `isRetryableAuthFailure` — correctly, for what it was written for — admits
 * only 401/403/419, so a busy 500 fell to the `'not-retryable'` branch: no retry, an alert, and a
 * sale recorded with no fiscal receipt.
 *
 * WHY RE-SENDING IS SAFE HERE, AND ONLY HERE. Wipon's own ledger was read back over `GET /ticket`
 * for that cashbox and that day: **53 sale tickets, and Firestore holds exactly the same 53** —
 * not one ticket punched that Atlas does not know about. A refusal carrying this message did not
 * record anything. That is the entire licence for the retry, and it does not extend one inch
 * further: a bare 500, a 502, a timeout or a dropped connection may mean the receipt WAS punched
 * and the reply was lost, and `POST /ticket` takes no idempotency key, so a duplicate could never
 * be collapsed afterwards. `isCashboxBusy` therefore keys on the provider's MESSAGE and ignores
 * the status entirely — which is what stops this from ever being widened to "5xx is retryable" by
 * someone reading the status column alone.
 *
 * WHY THE TILL WAS PROVOKING IT. Wipon answers a ticket in p50 ≈ 3.7s / p90 ≈ 6.8s (339 tickets;
 * note their `created_at` is Almaty local time labelled `Z`, so the naive lag reads ≈18000s too
 * high). Sales in a rush arrive 1.3–3s apart and `saveSaleToOFD` is deliberately not awaited by
 * NewSaleScreen, so the till held several ticket POSTs in flight against one cashbox. It was
 * manufacturing the contention. `withCashboxTurn` sends one at a time per cashbox instead.
 */

/**
 * How long to wait before re-sending a ticket the cashbox refused as busy, per attempt.
 *
 * Five attempts across 30.5s of base delay — up to ≈38.1s once the jitter below is added, which is
 * the figure the queue cap is derived from. Sized against the incident rather than against the p90
 * service time: for each of the 15-sale burst on 2026-09-07, the interval from the sale to the
 * next ticket that cashbox actually punched was 0.2s–12s for eight of them, 15–28s for four more,
 * and 52s–190s for the rest. A ≈14.5s budget would have left a third of the recoverable ones on
 * the floor for the sake of 16 seconds.
 *
 * It is deliberately NOT stretched to cover the 190s stalls. Two reasons: a turn is held for the
 * whole backoff, so a longer budget parks every later sale behind this one; and those stalls were
 * measured while the till itself was firing several tickets at once at a provider that serialises
 * them — `withCashboxTurn` removes that load, so the deep congestion this budget declines to chase
 * is largely congestion we were creating. What this does not absorb is still visible in Sales
 * History, whose retry banner re-signs it on demand.
 */
export const WIPON_BUSY_RETRY_DELAYS_MS = [1500, 4000, 9000, 16000]

/**
 * How much random extra is added to each backoff, so two tills sharing a cashbox do not line their
 * retries up on each other. Exported because `WIPON_CASHBOX_QUEUE_MAX_WAIT_MS` is derived from it.
 */
export const WIPON_BUSY_RETRY_JITTER = 0.25

/** The longest a single turn can spend asleep between attempts: ≈38.1s. */
export const WIPON_BUSY_RETRY_MAX_BACKOFF_MS = Math.ceil(
  WIPON_BUSY_RETRY_DELAYS_MS.reduce((total, delay) => total + delay, 0) * (1 + WIPON_BUSY_RETRY_JITTER),
)

/**
 * The longest a sale will wait for its turn at a cashbox before going anyway.
 *
 * Serialising creates a failure mode the old code could not have: one request that never settles
 * would park every later sale behind it for the rest of the shift. Rather than abort the request
 * in flight — it may be punching a legal document, and aborting it manufactures exactly the
 * ambiguous outcome this file refuses to retry — we stop WAITING for it. Past the cap the till
 * degrades to the concurrent behaviour it had before this change, which is survivable; a till that
 * has silently stopped signing receipts is not.
 *
 * The cap is DERIVED, not chosen, and that is the second review finding on this change. It was a
 * flat 60s while a fully-retrying head can legitimately hold its turn for `MAX_BACKOFF` (≈38.1s)
 * plus a request round trip per attempt — so the first waiter breached the cap and posted
 * concurrently *exactly* when the busy retry had engaged, which is the one moment the serialisation
 * exists for. Anything below the head's worst-case hold switches the queue off under load. Deriving
 * it from the backoff keeps the two from drifting apart again; `wiponCashboxBusy.atl1237.test.js`
 * asserts the invariant so a future change to the delays cannot quietly reintroduce it.
 *
 * It is a PER-HOP cap, not a total from enqueue, and the difference is the third review finding.
 * Every waiter used to start its own timer the moment it joined the queue, so the caps ran
 * CONCURRENTLY: with a head legitimately holding ~60s and sales arriving 1.3–3s apart, queue
 * positions 3 and beyond all breached within a second of each other and posted together. Raising
 * the cap cannot fix that — the derivation above only ever protected waiter #1. `withCashboxTurn`
 * therefore waits for its predecessor to actually TAKE the cashbox before starting its own clock,
 * so the cap measures one hop. A deep queue can consequently wait more than this in total, which
 * is the right trade: the alternative is the serialisation switching itself off under exactly the
 * congestion it exists for.
 */
export const WIPON_CASHBOX_QUEUE_MAX_WAIT_MS = WIPON_BUSY_RETRY_MAX_BACKOFF_MS + 50000

/**
 * cashbox key → the tail of the queue: `{started, done}` for the most recently enqueued turn.
 * `started` resolves when that turn actually takes the cashbox, `done` when it lets go of it.
 */
const cashboxQueues = new Map()

/**
 * The queue a set of credentials belongs to. Exported so a caller that reasons about "this
 * cashbox" — `SalesHistoryScreen`'s per-cashbox busy break — partitions the world exactly the way
 * the queue does, rather than keeping a second copy of this expression that can drift from it.
 */
export const cashboxQueueKey = (credentials) =>
  `${credentials?.profile?.id ?? 'no-profile'}:${credentials?.cashbox?.id ?? 'no-cashbox'}`

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Settle when `promise` does, or when `ms` elapses — whichever is first, and never by rejecting.
 * The timer is cleared on the winning path so a drained queue leaves nothing pending behind it.
 */
const settleOrTimeout = (promise, ms) =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms)
    const done = () => { clearTimeout(timer); resolve() }
    promise.then(done, done)
  })

/**
 * Run `task` with at most one ticket request in flight for this cashbox.
 *
 * Keyed by profile+cashbox rather than globally: a Wipon profile can own several cashboxes (Petit
 * runs four stores on profile 5319), and one slow store must not stop the others from fiscalizing.
 *
 * A turn exposes TWO promises. `done` settles when the ticket is finished; `started` settles when
 * this turn actually takes the cashbox. A waiter blocks on its predecessor's `started` before
 * arming `WIPON_CASHBOX_QUEUE_MAX_WAIT_MS` against its `done`, which is what keeps the caps from
 * running concurrently and expiring together — see that constant's docblock.
 */
const withCashboxTurn = async (credentials, task) => {
  const key = cashboxQueueKey(credentials)
  const predecessor = cashboxQueues.get(key)
  let release
  let begin
  const mine = {
    done: new Promise((resolve) => { release = resolve }),
    started: new Promise((resolve) => { begin = resolve }),
  }
  cashboxQueues.set(key, mine)
  if (predecessor) {
    // ATL-1237 review 4. The cap measures how long THIS turn waits for the one immediately ahead
    // of it, so its clock starts when that turn actually takes the cashbox — not when this one
    // joined the queue. Timing from enqueue ran every waiter's cap concurrently: with four sales
    // queued behind a head that legitimately holds for ~60s (MAX_BACKOFF plus a round trip per
    // attempt), positions three and four breached the cap at almost the same instant and posted
    // together — turning the serialisation off under exactly the congestion it exists for, and
    // doing it no matter how large the cap was made. `started` always settles: the head resolves
    // it with no wait at all, and every later turn is bounded by the cap against the one before,
    // so the chain cannot stall.
    await predecessor.started
    await settleOrTimeout(predecessor.done, WIPON_CASHBOX_QUEUE_MAX_WAIT_MS)
  }
  begin()
  try {
    return await task()
  } finally {
    // Always, including when the ticket failed: a failure must free the cashbox, not wedge it.
    release()
    if (cashboxQueues.get(key) === mine) cashboxQueues.delete(key)
  }
}

/**
 * POST a ticket, waiting for this cashbox's turn and re-sending only a refusal the provider
 * itself described as temporary.
 *
 * Every ticket the app creates goes through here — sales, the rounding retry, and both return
 * paths — because they all contend for the same single-threaded cashbox.
 */
const postTicket = async (credentials, data) =>
  withCashboxTurn(credentials, async () => {
    let lastError
    for (let attempt = 0; attempt <= WIPON_BUSY_RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) {
        // Jitter so that two tills sharing a cashbox do not line their retries up on each other.
        const base = WIPON_BUSY_RETRY_DELAYS_MS[attempt - 1]
        await wait(base + Math.floor(Math.random() * base * WIPON_BUSY_RETRY_JITTER))
      }
      try {
        return await axios.request({
          url: 'profile/' + credentials.profile.id + '/cashbox/' + credentials.cashbox.id + '/ticket',
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + credentials.accessToken,
          },
          baseURL: BASE_URL,
          data,
        })
      } catch (error) {
        lastError = error
        // Anything else is either permanent or ambiguous, and both are the caller's to report.
        if (!isCashboxBusy(error)) throw error
      }
    }
    throw lastError
  })

export const getAccessToken = async (credentials) => {
  try {
    const response = await axios.request({
      url: '/login',
      method: 'post',
      baseURL: BASE_URL,
      data: qs.stringify({
        login: credentials.username,
        password: credentials.password, 
      })
    })
    return response.data.data.access_token 
  } catch (error) {
    handleError(error)
  }
}

export const getProfiles = async (credentials) => {
  try {
    const response = await axios.request({
      url: '/profile',
      method: 'get',
      headers: {
        Authorization: 'Bearer ' + credentials.accessToken,
      },
      baseURL: BASE_URL,
    })
    return response?.data?.data
  } catch (error) {
    handleError(error)
  }
}

export const getCashboxes = async (credentials) => {
  try {
    const response = await axios.request({
      url: '/profile/' + credentials.profile.id + '/common/cashbox',
      method: 'get',
      headers: {
        Authorization: 'Bearer ' + credentials.accessToken,
      },
      baseURL: BASE_URL,
    })
    return response.data.data
  } catch (error) {
    handleError(error)
  }
}

export const getSalePoints = async (credentials) => {
  try {
    const response = await axios.request({
      url: '/profile/' + credentials.profile.id + '/programming-mode/sale-point',
      method: 'get',
      headers: {
        Authorization: 'Bearer ' + credentials.accessToken,
      },
      baseURL: BASE_URL,
    })
    return response.data.data
  } catch (error) {
    handleError(error)
  }
}

export const getSaleSections = async (credentials) => {
  try {
    const response = await axios.request({
      url: '/profile/' + credentials.profile.id + '/programming-mode/sale-section',
      method: 'get',
      headers: {
        Authorization: 'Bearer ' + credentials.accessToken,
      },
      baseURL: BASE_URL,
    })
    return response.data.data
  } catch (error) {
    handleError(error)
  }
}

export const getActiveShift = async (credentials) => {
  try {
    const response = await axios.request({
      url: 'profile/' + credentials.profile.id + '/cashbox/' + credentials.cashbox.id + '/shift/active',
      method: 'get',
      headers: {
        Authorization: 'Bearer ' + credentials.accessToken,
      },
      baseURL: BASE_URL,
    })
    return response.data.data
  } catch (error) {
    handleError(error)
  }
}

/**
 * Saves a sale transaction to the Wipon API and returns the receipt details
 * 
 * @param {Object} credentials - Object containing access token, profile, cashbox, and sale section details
 * @param {Object} cartInfo - Object containing cart items, payment method, and other sale details
 * @param {Function} [onError] - Called with the axios error when the ticket could not be signed
 * @returns {Object} Ticket details with modified receipt text
 * 
 * Response structure:
 * {
 *   id: number                    // Unique identifier for the ticket
 *   card_sum: number              // Amount paid by card (in minor units - e.g. 1917000 = 19170.00)
 *   cash_sum: number              // Amount paid in cash (in minor units)
 *   cashbox_id: number            // ID of the cashbox where transaction occurred
 *   company_id: number            // ID of the company
 *   created_at: string            // Creation timestamp in ISO format
 *   deleted_at: string|null       // Deletion timestamp if applicable
 *   discount_quantity: number     // Number of discounts applied
 *   discount_sum: number          // Total discount amount (in minor units)
 *   document_number: number       // Document sequential number
 *   employee_id: number           // ID of the employee who processed the transaction
 *   iin: string|null              // IIN (Individual Identification Number) if provided
 *   link: string                  // URL to view the receipt online
 *   local_uuid: string|null       // Local unique identifier
 *   markup_quantity: number       // Number of markups applied
 *   markup_sum: number            // Total markup amount (in minor units)
 *   ofd_qr: string                // OFD (Online Fiscal Data Operator) QR code URL
 *   offline: boolean              // Whether transaction was processed offline
 *   offline_receipt_number: string // Receipt number for offline transactions
 *   platform: string|null         // Platform identifier
 *   receipt_number: string        // Official receipt number
 *   shift_id: number              // ID of the shift during which transaction occurred
 *   sum: number                   // Total transaction amount (in minor units)
 *   transaction_id: number|null   // External transaction ID if applicable
 *   type: Object                  // Transaction type (e.g. {id: 2, name: 'Продажа'} for sales)
 *   updated_at: string            // Last update timestamp in ISO format
 *   uuid: string                  // Unique identifier (UUID format)
 *   wipon_ref_id: string|null     // Reference ID in the Wipon system
 *   check: string                 // Modified receipt text with ATLAS branding (HTML format)
 * }
 */
export const saveSale = async (credentials, cartInfo, onError = null) => {
  var items = []
  var itemsSum = 0
  var punchCardUsed = false
  for (const value of cartInfo.cartItems) {
    // Get the correct base price based on selected price type
    const basePrice = getProductPrice(value, cartInfo.store, cartInfo.priceType, null, cartInfo.priceTypes) || value.salePrices?.[cartInfo.store] || 0
    
    var discount = (basePrice * value.quantity - value.finalItemPrice)  // individual cart item and whole cart discounts
    var modifierSurplus = 0
    if (discount < 0) {
      modifierSurplus = -discount
      discount = 0
    }
    // bonus reduction
    if (cartInfo.bonusReduction) {
      discount += (cartInfo.bonusReduction/cartInfo.cartItems.length)
    }
    // punch card reduction
    if (!punchCardUsed && cartInfo.punchCardReduction && value.finalItemPrice === cartInfo.punchCardReduction) {
      discount += cartInfo.punchCardReduction
      punchCardUsed = true
    }
    const newItem = {
      "name": value.name,
      "quantity": value.quantity,
      "price": parseInt((basePrice + modifierSurplus/(value.quantity || 1)).toFixed(0)),
      "discount": discount,
      "unit_id": value.type ? 3 : 1,
      "sale_section_id": credentials.saleSection.id,
      "vat_type": credentials.vatType,
    }
    itemsSum += (newItem.price * newItem.quantity) - discount
    items.push(newItem) 
  }
  var payments = [
    {
      "payment_method": (cartInfo.paymentMethod.isCash ? 0 : 1),
      "sum": itemsSum
    },
  ]
  if (cartInfo.mixedPayment && cartInfo.paymentMethod.isCash !== cartInfo.secondPaymentMethod.isCash) {
    payments = [
      {
        "payment_method": (cartInfo.paymentMethod.isCash ? 0 : 1),
        "sum": cartInfo.paymentMethod.value
      },
      {
        "payment_method": (cartInfo.secondPaymentMethod.isCash ? 0 : 1),
        "sum": itemsSum - cartInfo.paymentMethod.value
      },
    ]
  }
  try {
    const response = await postTicket(credentials, {
      "type": 2,
      "items": items,
      "payments": payments,
      "sale_point_id": credentials.salePoint.id
    })
    const check = response.data.data.check
    return {...response.data.data, check}
  } catch (error) {
    if (error?.response?.status === 400 && error.response.data?.message === "Ошибка, сдача не может быть отрицательной, проверьте вводимые Вами значения") {
      let paymentsRetry = [
        {
          "payment_method": (cartInfo.paymentMethod.isCash ? 0 : 1),
          "sum": itemsSum + 1
        },
      ]
      // ATL-1170 review: this rounding retry used to sit in the catch block with no try of its own,
      // so any failure of the SECOND request rejected `saveSale` itself rather than resolving to
      // `undefined` like every other failure. That defeats the contract `fiscalizeSale` now
      // publishes — no `reason`, no Sentry breadcrumb at the NewSale call site (which does not
      // await `saveSaleToOFD`, so it became an unhandled rejection) and a retry button in
      // SaleDetailScreen whose spinner never cleared. A failed sign is reported, never thrown.
      try {
        const retryResponse = await postTicket(credentials, {
          "type": 2,
          "items": items,
          "payments": paymentsRetry,
          "sale_point_id": credentials.salePoint.id
        })
        const retryCheck = retryResponse.data.data.check
        return {...retryResponse.data.data, check: retryCheck}
      } catch (retryError) {
        handleError(retryError)
        if (onError) onError(retryError)
      }
    } else {
      handleError(error)
      if (onError) onError(error)
    }
  }
}

/**
 * The statuses that mean the provider REFUSED the request, so re-sending it cannot double-sign.
 *
 * 401 is the measured one: on 2026-09-04 `api.kassa.wipon.kz` answered
 * `401 {"error":"Unauthenticated."}` both for a token minted by the retired host and for a request
 * carrying no token at all — which is the entire population this retry exists for, since every
 * stored token predates the host move and 10 of 23 stores had lost theirs altogether. 403 and 419
 * are included because a revoked or expired session can surface as either, and they carry the same
 * guarantee: the ticket was rejected, not recorded.
 */
export const WIPON_AUTH_FAILURE_STATUSES = [401, 403, 419]

/**
 * Whether a failed ticket request may be re-sent.
 *
 * ATL-1170 review, and the one decision in this change that could make an outage WORSE. A retry
 * that fires on *any* failure fires on a timeout and on a 5xx — the two cases where Wipon may have
 * punched the receipt and merely failed to tell us. Re-sending then puts a **second fiscal document
 * for one sale** on the merchant's own cash register, and the ticket endpoint takes no idempotency
 * key, so nothing downstream can collapse them. That is a tax exposure we would have created.
 *
 * So the retry is narrowed to responses that prove the request was refused. The asymmetry is what
 * decides it: if this predicate is too narrow the till behaves as it did before this ticket — one
 * attempt — except that the failure is now loud (an alert at the till and a Sentry event) and the
 * merchant still has the Sales History retry, which re-logs-in before its first attempt. If it is
 * too wide, a merchant's books carry a duplicate receipt nobody can withdraw.
 *
 * An error with no `response` — a timeout, a dropped connection — is deliberately NOT retryable.
 * That is precisely the ambiguous case.
 */
export const isRetryableAuthFailure = (error) => {
  const status = error?.response?.status
  return typeof status === 'number' && WIPON_AUTH_FAILURE_STATUSES.indexOf(status) !== -1
}

/**
 * Log in again and return credentials carrying the new access token, or `null` if the login failed.
 *
 * ATL-1170 — every call site used to spell this inline as
 *
 *     let token = await getAccessToken(credentials)
 *     let newCredentials = {...credentials, accessToken: token}
 *     updateStoreWiponCredentials(store.ref, newCredentials)
 *
 * and `getAccessToken` returns `undefined` on failure. Two of the four call sites (SaleDetailScreen
 * and SalesHistoryScreen) had grown an `if (token)` guard; the two that had not — NewSaleScreen's
 * `OFDStart` and ShiftScreen — went on to build `{...credentials, accessToken: undefined}`, an
 * object that is truthy, and wrote it back over credentials that were working. A provider outage
 * must not cost the store its configuration, so there is now one function and the guard is in it.
 */
export const refreshCredentials = async (credentials) => {
  if (!credentials) return null
  const accessToken = await getAccessToken(credentials)
  if (!accessToken) return null
  return {...credentials, accessToken}
}

/**
 * Fiscalize a sale, re-issuing the access token once if the first attempt is refused.
 *
 * ATL-1170 — the sale path had no recovery at all. `saveSaleToOFD`'s light-kassa branch has always
 * done `if (!fiscalInfo) { setNewLKTokens(...); retry }`, and SaleDetailScreen's manual retry
 * button does the same for wipon; the branch that fiscalizes an actual sale did neither. It called
 * `saveSale` exactly once and, because `saveSale` swallows its error and resolves to `undefined`,
 * `if (fiscalInfo)` then skipped the receipt in complete silence — no retry, no Sentry, no alert.
 * That is how 67 receipts went unsigned across three organizations before a customer noticed.
 *
 * The retry matters beyond the outage that exposed it: when the host moved, every stored access
 * token had been minted by the old host, so the first sale on each till after the fix would be
 * refused by a server that is up and credentials that are valid. The retry carries the fresh token
 * in memory rather than re-reading the store, because no listener can have observed the write yet.
 *
 * ATL-1274 — this function no longer decides how the failure is SHOWN. It used to call
 * `alertCashier` on three of its five give-up paths and hand `showAlert: true` to its second
 * `saveSale`, i.e. a blocking `alert()` raised from inside the provider module, on the sale path,
 * once per sale. Petit Зенкова met that 13 times in a day while Wipon reported the cashbox
 * blocked: every one of those sales WAS saved, and every one of those dialogs stopped the queue to
 * say so ambiguously. The outcome is now RETURNED — `reason` for the caller's logic, `message` for
 * the caller's words — and each surface renders it the way that surface should: New Sale as a
 * non-blocking failed state beside the saved-sale toast, Sale Detail as the dialog its explicit
 * retry button has always produced.
 *
 * @param {Object} credentials - the store's `wiponCredentials`
 * @param {Object} cartInfo - the sale, in the shape `saveSale` expects
 * @param {Function} [persistCredentials] - called with refreshed credentials so the NEXT sale
 *   skips the login. It is deliberately NOT awaited, and its failure is logged and ignored: it
 *   cannot be allowed to lose a receipt that the OFD has already issued.
 * @returns {{fiscalInfo: Object|null, credentials: Object|null, reason: string|null,
 *   message: string|null, responseClass: string|null, rejection: Object|null}} `rejection` is
 *   `summarizeWiponRejection` of the same failure (allowlisted code/message, never the body).
 *   `responseClass` is
 *   `classifyWiponResponse` of the failure Wipon answered (ATL-2035), `null` on success and on the
 *   two reasons Wipon never spoke on. `reason` is `null` on success, else `'no-credentials'`,
 *   `'cashbox-busy'` (ATL-1237 — the bounded re-send inside `saveSale` was exhausted),
 *   `'not-retryable'`, `'login-failed'` or `'rejected'`.
 *
 *   `message` is `describeWiponError`'s short description, and is `null` for the two reasons Wipon
 *   never spoke on — `'no-credentials'` and `'login-failed'` — which the UI localises from the
 *   reason instead. It is NOT guaranteed to be the provider's own words, and this doc said it was
 *   until review caught it: `describeWiponError` falls back to hardcoded Russian when the request
 *   got no response at all («нет ответа от сервера ОФД») or carried no usable error
 *   («неизвестная ошибка»), so an English-locale till sees those two Russian phrases inside an
 *   otherwise localised sentence. That is pre-existing — the alert this replaced was Russian in
 *   every locale — and Russian is the product's primary locale, so it is left as ATL-1276 rather
 *   than fixed here: the honest fix needs a reason that distinguishes "the provider refused" from
 *   "the provider never answered", which widens the classification on a fiscal path.
 *   See `utils/unfiscalizedSales.ts`.
 */
export const fiscalizeSale = async (credentials, cartInfo, persistCredentials) => {
  if (!credentials) {
    return {fiscalInfo: null, credentials: null, reason: 'no-credentials', message: null, responseClass: null, rejection: null}
  }

  let firstError = null
  let fiscalInfo = await saveSale(credentials, cartInfo, (error) => { firstError = error })
  if (fiscalInfo) return {fiscalInfo, credentials, reason: null, message: null, responseClass: null, rejection: null}

  // Only an outright refusal may be re-sent — see `isRetryableAuthFailure`. Everything else is
  // reported to the caller here, because the first attempt was deliberately silent.
  if (!isRetryableAuthFailure(firstError)) {
    return {
      fiscalInfo: null,
      credentials,
      message: describeWiponError(firstError),
      // ATL-1237: a busy cashbox has already been re-sent up to
      // `WIPON_BUSY_RETRY_DELAYS_MS.length` times inside `saveSale`, and re-logging in would not
      // help — the token is fine, the register is occupied. Naming it separately is what makes the
      // difference countable in Sentry: 152 events said `not-retryable` while the provider had in
      // fact been asking to be retried the whole time.
      reason: isCashboxBusy(firstError) ? 'cashbox-busy' : 'not-retryable',
      responseClass: classifyWiponResponse(firstError),
      rejection: summarizeWiponRejection(firstError),
    }
  }

  const refreshed = await refreshCredentials(credentials)
  if (!refreshed) {
    return {fiscalInfo: null, credentials, reason: 'login-failed', message: null, responseClass: null, rejection: null}
  }

  if (persistCredentials) {
    // ATL-1170 review: deliberately NOT awaited. The one call site is
    // `updateStoreWiponCredentials`, whose Firestore `update()` resolves only when the SERVER acks
    // the write — on a till that can reach Wipon but not Firestore, that promise never settles, and
    // awaiting it here held the receipt-producing attempt below behind it. The sale is already
    // committed at that point, and `saveSaleToOFD` is not awaited by its caller, so the outcome
    // would have been an unsigned receipt with no alert and no Sentry event: the exact failure this
    // function exists to end. Saving the token only spares the NEXT sale a login.
    try {
      Promise.resolve(persistCredentials(refreshed)).catch((error) => {
        console.warn('[wipon] could not persist refreshed credentials', error)
      })
    } catch (error) {
      console.warn('[wipon] could not persist refreshed credentials', error)
    }
  }

  // Second and last attempt. By this point the cashier is holding a receipt that is not fiscal,
  // and that is not something to discover from the sales list tomorrow — so the failure is
  // reported, in the provider's own words, to whoever asked for the fiscalization.
  let secondError = null
  fiscalInfo = await saveSale(refreshed, cartInfo, (error) => { secondError = error })
  return {
    fiscalInfo: fiscalInfo || null,
    credentials: refreshed,
    // A stale token and a busy cashbox can arrive in that order — the login succeeds and the
    // re-sent ticket then exhausts its own busy retries. Reporting that as 'rejected' would leave
    // it out of the very Sentry count this reason exists to create.
    reason: fiscalInfo ? null : (isCashboxBusy(secondError) ? 'cashbox-busy' : 'rejected'),
    message: fiscalInfo ? null : describeWiponError(secondError),
    responseClass: fiscalInfo ? null : classifyWiponResponse(secondError),
    rejection: fiscalInfo ? null : summarizeWiponRejection(secondError),
  }
}

export const saveReturnFromDelete = async (credentials, cartInfo) => {
  var items = []
  var punchCardUsed = false
  for (const value of cartInfo.cartItems) {
    // Get the correct base price based on selected price type
    const basePrice = getProductPrice(value, cartInfo.store, cartInfo.priceType, null, cartInfo.priceTypes) || value.salePrices?.[cartInfo.store] || 0
    
    var discount = (basePrice * value.quantity - value.finalItemPrice)  // individual cart item and whole cart discounts
    // bonus reduction
    if (cartInfo.bonusReduction) {
      discount += (cartInfo.bonusReduction/cartInfo.cartItems.length)
    }
    // punch card reduction
    if (!punchCardUsed && cartInfo.punchCardReduction && value.finalItemPrice === cartInfo.punchCardReduction) {
      discount += cartInfo.punchCardReduction
      punchCardUsed = true
    }
    const newItem = {
      "name": value.name,
      "quantity": value.quantity,
      "price": basePrice - discount,
      "discount": 0,
      "unit_id": value.type ? 3 : 1,
      "sale_section_id": credentials.saleSection.id,
      "vat_type": credentials.vatType,
    }
    items.push(newItem) 
  }
  var payments = [
    {
      "payment_method": (cartInfo.paymentMethod.isCash ? 0 : 1),
      "sum": cartInfo.finalTotalCartPrice
    },
  ]
  if (cartInfo.mixedPayment) {
    payments = [
      {
        "payment_method": (cartInfo.paymentMethod.isCash ? 0 : 1),
        "sum": cartInfo.paymentMethod.value
      },
      {
        "payment_method": (cartInfo.secondPaymentMethod.isCash ? 0 : 1),
        "sum": cartInfo.secondPaymentMethod.value
      },
    ]
  }
  try {
    var response = await postTicket(credentials, {
      "type": 3,
      "items": items,
      "payments": payments,
      "sale_point_id": credentials.salePoint.id
    })
    var check = response.data.data.check
    return {...response.data.data, check}
  } catch (error) {
    handleError(error)
  }
}

export const saveReturn = async (credentials, cartInfo) => {
  var items = []
  for (const value of cartInfo.cartItems) {
    if (value.isReturned) {
      // Get the correct base price based on selected price type
      const basePrice = getProductPrice(value, cartInfo.store, cartInfo.priceType, null, cartInfo.priceTypes) || value.salePrices?.[cartInfo.store] || 0
      
      var discount = (basePrice * value.returnedQuantity - value.returnedAmount)  // individual cart item and whole cart discounts
      const newItem = {
        "name": value.name,
        "quantity": value.returnedQuantity,
        "price": basePrice - discount,
        "discount": 0,
        "unit_id": value.type ? 3 : 1,
        "sale_section_id": credentials.saleSection.id,
        "vat_type": credentials.vatType,
      }
      items.push(newItem) 
    }
  }
  try {
    var response = await postTicket(credentials, {
      "type": 3,
      "items": items,
      "payments": [
        {
          "payment_method": (cartInfo.paymentMethod.isCash ? 0 : 1),
          "sum": cartInfo.finalTotalCartPrice
        }
      ],
      "sale_point_id": credentials.salePoint.id
    })
    var check = response.data.data.check
    return {...response.data.data, check}
  } catch (error) {
    handleError(error)
  }
}


export const closeCashboxShift = async (credentials) => {
  try {
    const response = await axios.request({
      url: 'profile/' + credentials.profile.id + '/cashbox/' + credentials.cashbox.id + '/shift/close',
      method: 'patch',
      headers: {
        Authorization: 'Bearer ' + credentials.accessToken,
      },
      baseURL: BASE_URL,
    })
    return response.data.data
  } catch (error) {
    handleError(error)
  }
}

export const extractCash = async (credentials, sum) => {
  try {
    const response = await axios.request({
      url: 'profile/' + credentials.profile.id + '/cashbox/' + credentials.cashbox.id + '/money-movement',
      method: 'post',
      headers: {
        Authorization: 'Bearer ' + credentials.accessToken,
      },
      baseURL: BASE_URL,
      data: {
        "type": 1, 
        "sum": sum 
      }
    })
    return response.data.data
  } catch (error) {
    handleError(error)
  }
}

export const getTickets = async (credentials) => {
  try {
    const response = await axios.request({
      url: 'profile/' + credentials.profile.id + '/cashbox/' + credentials.cashbox.id + '/ticket',
      method: 'get',
      headers: {
        Authorization: 'Bearer ' + credentials.accessToken,
      },
      baseURL: BASE_URL,
    })
    return response.data
  } catch (error) {
    handleError(error)
  }
}

/**
 * Get details of a specific ticket by ID
 * 
 * @param {Object} credentials - Object containing access token, profile and cashbox details
 * @param {string} ticketId - ID of the ticket to fetch
 * @returns {Object} Ticket details object
 * 
 * Response structure:
 * {
 *   id: number                    // Unique identifier for the ticket
 *   card_sum: number              // Amount paid by card (in minor units - e.g. 1917000 = 19170.00)
 *   cash_sum: number              // Amount paid in cash (in minor units)
 *   cashbox_id: number            // ID of the cashbox where transaction occurred
 *   company_id: number            // ID of the company
 *   created_at: string            // Creation timestamp in ISO format
 *   deleted_at: string|null       // Deletion timestamp if applicable
 *   discount_quantity: number     // Number of discounts applied
 *   discount_sum: number          // Total discount amount (in minor units)
 *   document_number: number       // Document sequential number
 *   employee_id: number           // ID of the employee who processed the transaction
 *   iin: string|null              // IIN (Individual Identification Number) if provided
 *   link: string                  // URL to view the receipt online
 *   local_uuid: string|null       // Local unique identifier
 *   markup_quantity: number       // Number of markups applied
 *   markup_sum: number            // Total markup amount (in minor units)
 *   ofd_qr: string                // OFD (Online Fiscal Data Operator) QR code URL
 *   offline: boolean              // Whether transaction was processed offline
 *   offline_receipt_number: string // Receipt number for offline transactions
 *   platform: string|null         // Platform identifier
 *   receipt_number: string        // Official receipt number
 *   shift_id: number              // ID of the shift during which transaction occurred
 *   sum: number                   // Total transaction amount (in minor units)
 *   transaction_id: number|null   // External transaction ID if applicable
 *   type: Object                  // Transaction type (e.g. {id: 2, name: 'Продажа'} for sales)
 *   updated_at: string            // Last update timestamp in ISO format
 *   uuid: string                  // Unique identifier (UUID format)
 *   wipon_ref_id: string|null     // Reference ID in the Wipon system
 * }
 */
export const getTicketById = async (credentials, ticketId) => {
  try {
    const response = await axios.request({
      url: 'profile/' + credentials.profile.id + '/cashbox/' + credentials.cashbox.id + '/ticket/' + ticketId,
      method: 'get',
      headers: {
        Authorization: 'Bearer ' + credentials.accessToken,
      },
      baseURL: BASE_URL,
    })
    return response.data.data
  } catch (error) {
    handleError(error)
  }
}

/**
 * The provider's own words for a failure, or `null` if it did not give any.
 *
 * Wipon spells it `message` on a business rejection («Смена не открыта») and `error` on an auth
 * one (`{"error":"Unauthenticated."}`, measured on 2026-09-04), so both are read.
 */
const wiponMessage = (error) => {
  const data = error?.response?.data
  if (data && typeof data === 'object') {
    for (const key of ['message', 'error']) {
      if (typeof data[key] === 'string' && data[key]) return data[key]
    }
  }
  return null
}

/**
 * Whether the provider refused this ticket because the cashbox was busy — the one refusal that is
 * safe to send again.
 *
 * ATL-1237. This deliberately reads the MESSAGE and not the status. Production returned **500** for
 * this and **400** for unrelated rejections on the same endpoint inside the same minute, so the
 * status carries no meaning here; and keying on the status is how a narrow, evidenced retry would
 * turn into "5xx is retryable", which is the one change that could put a **second fiscal document
 * for one sale** on a merchant's register. See the block at the top of this file for the ledger
 * read that establishes a busy refusal records nothing.
 *
 * Matching is on «Касса в обработке» as an ORDERED phrase — the subject immediately followed by
 * the state — so case, spacing, punctuation and declension do not break it. The order is
 * load-bearing and was the first review finding on this change: testing for the two fragments
 * independently («касс» anywhere AND «в обработке» anywhere) also matches a sentence that says the
 * opposite, e.g. «Чек в обработке в кассе» — the TICKET is being processed, i.e. accepted. Re-sending
 * that is the duplicate fiscal document this whole file is arranged to prevent. The subject of the
 * sentence must be the cashbox.
 *
 * If Wipon ever rewords it entirely this stops matching and the till is back to its pre-ATL-1237
 * behaviour: one attempt, loudly reported. That is the correct direction to fail in.
 */
// `i` because this is exported: `isCashboxBusy` lowercases before testing, but a caller
// reaching for the pattern directly would otherwise silently miss «Касса в обработке».
export const WIPON_CASHBOX_BUSY_PATTERN = /касс\S*\s+в\s+обработке/i

export const isCashboxBusy = (error) => {
  const message = wiponMessage(error)
  if (!message) return false
  return WIPON_CASHBOX_BUSY_PATTERN.test(message.toLowerCase().replace(/\s+/g, ' '))
}

/** «Смена не открыта» — the merchant has not opened the shift on the Wipon cashbox. */
export const WIPON_SHIFT_NOT_OPEN_PATTERN = /смен\S*\s+не\s+открыт/i

/**
 * ATL-2035 — WHAT KIND of answer a failed Wipon call got, for Sentry's `fiscalResponseClass` tag.
 *
 * `'not-retryable'` alone cannot tell «the merchant has not opened the shift» from «Wipon is
 * down», and the first is something the merchant fixes while the second is ours to escalate.
 * The provider's own text is NOT sent: it is free text from a third party and may name the
 * merchant, the cashier or the cashbox. Only this closed set leaves the device:
 *   'shift-not-open' | 'cashbox-busy' | 'http-4xx-<status>' | 'http-5xx-<status>' |
 *   'http-<status>' (any other status) | 'network' (no response) | 'unknown' (no request at all,
 *   e.g. a throw while building it).
 * The shift and busy messages are checked before the status, because Wipon returns them under
 * different statuses within the same minute (see `isCashboxBusy`).
 */
export const classifyWiponResponse = (error) => {
  const message = wiponMessage(error)
  if (message) {
    const normalized = message.toLowerCase().replace(/\s+/g, ' ')
    if (WIPON_SHIFT_NOT_OPEN_PATTERN.test(normalized)) return 'shift-not-open'
    if (WIPON_CASHBOX_BUSY_PATTERN.test(normalized)) return 'cashbox-busy'
  }
  const status = error?.response?.status
  if (Number.isInteger(status)) {
    if (status >= 400 && status < 500) return `http-4xx-${status}`
    if (status >= 500 && status < 600) return `http-5xx-${status}`
    return `http-${status}`
  }
  if (error?.request) return 'network'
  return 'unknown'
}

/** Fields of a Wipon error body that name the ERROR, never the merchant. Anything else is dropped. */
const WIPON_REJECTION_CODE_FIELDS = ['code', 'error_code', 'errorCode']
/** A code is an identifier: short, no spaces. A 12-digit ИИН/БИН is longer than any code. */
const WIPON_REJECTION_CODE_PATTERN = /^[A-Za-z0-9_.-]{1,32}$/
const WIPON_REJECTION_CODE_MAX_DIGITS = 6
export const WIPON_REJECTION_MESSAGE_MAX_LENGTH = 120
const WIPON_REJECTION_MAX_FIELDS = 5
const WIPON_REJECTION_FIELD_PATTERN = /^[A-Za-z][A-Za-z0-9_.]{0,39}$/

const safeRejectionCode = (value) => {
  if (typeof value === 'number' && Number.isInteger(value)) value = String(value)
  if (typeof value !== 'string' || !WIPON_REJECTION_CODE_PATTERN.test(value)) return null
  if ((value.match(/\d/g) || []).length > WIPON_REJECTION_CODE_MAX_DIGITS) return null
  return value
}

/**
 * A provider message is kept only when it cannot carry a number: a digit is how an ИИН/БИН, a
 * phone, a receipt sum, a cashbox serial or a token would arrive. The same goes for `@` (an email)
 * and a URL. A message failing any of that is dropped WHOLE — nothing is cut out of it, because a
 * redacted sentence still leaks its remainder. Over-long messages are dropped, not truncated.
 */
const safeRejectionMessage = (value) => {
  if (typeof value !== 'string') return null
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (!normalized || normalized.length > WIPON_REJECTION_MESSAGE_MAX_LENGTH) return null
  if (/\d|@|:\/\/|www\./i.test(normalized)) return null
  return normalized
}

/**
 * ATL-2035 — a safe digest of what Wipon said when it refused, for the Sentry event.
 *
 * `classifyWiponResponse` names the KIND of answer; this keeps the two things that tell one 400
 * from another — the provider's error code and its message — and nothing else. It is an
 * ALLOWLIST of fields, not a scrub of the body: `code`/`error_code`/`errorCode`, `message` (or
 * `error`, Wipon's auth spelling), and the NAMES (never the values) of a validation `errors`
 * object. Every other field of the body — ИИН, names, phones, sums, tokens, the echoed ticket —
 * never leaves the device, whatever it is called. See `safeRejectionCode`/`safeRejectionMessage`
 * for what makes a code or a message admissible.
 *
 * Residual risk, stated rather than hidden: a digit-free message could still contain a word that
 * is a person's name. The cap and the digit rule bound it; the message is in `extra`, not a tag.
 *
 * @returns {{code: string|null, message: string|null, fields: string[]|null}|null} `null` when
 *   Wipon gave no JSON body at all.
 */
export const summarizeWiponRejection = (error) => {
  const data = error?.response?.data
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  let code = null
  for (const key of WIPON_REJECTION_CODE_FIELDS) {
    code = safeRejectionCode(data[key])
    if (code) break
  }
  const message = safeRejectionMessage(wiponMessage(error))
  let fields = null
  if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    const names = Object.keys(data.errors).filter((k) => WIPON_REJECTION_FIELD_PATTERN.test(k))
    if (names.length) fields = names.slice(0, WIPON_REJECTION_MAX_FIELDS)
  }
  return {code, message, fields}
}

/**
 * A short, true description of a failed Wipon call, for the cashier and for Sentry.
 *
 * ATL-1170 — the alert below used to be `'Ошибка в ОФД - ' + error.response.data.message`, which
 * has two failure modes and hit both on 2026-09-04. When the dead host answered with an HTML body
 * there is no `.message`, so the cashier would have been told «Ошибка в ОФД - undefined»; and when
 * a request gets no response at all `error.response` is undefined, so reading `.data` THREW inside
 * the error handler — replacing the alert with a crash on exactly the connectivity failure the
 * alert exists to report.
 */
export const describeWiponError = (error) => {
  const message = wiponMessage(error)
  if (message) return message
  if (error?.response) return 'HTTP ' + error.response.status
  if (error?.request) return 'нет ответа от сервера ОФД'
  return error?.message || 'неизвестная ошибка'
}

/**
 * ATL-1274 — the `alertCashier` that used to live here is gone, and with it the `showAlert`
 * parameter that threaded a UI decision through the provider module. It raised a blocking
 * `alert()` from inside an error handler on the sale path, so a cashbox that stayed blocked
 * interrupted the queue once per sale (13 of 13 sales, Petit Зенкова, 2026-09-08 and 09) while
 * saying nothing the sales list did not already say better. Failures are RETURNED from
 * `fiscalizeSale` now; the screens decide what to render. Nothing in this module talks to the
 * cashier any more, which is also why it no longer has to defend itself against runtimes that
 * have no `alert` global.
 */
const handleError = (error) => {
  if (error?.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.log(error.response.data)
    console.log(error.response.status)
    console.log(error.response.headers)
  } else if (error?.request) {
    // The request was made but no response was received
    console.log(error.request)
  } else {
    // Something happened in setting up the request that triggered an Error
    console.log('Error', error?.message)
  }
  console.log(error?.config)
}
